// src/services/authDebugUtils.ts
/**
 * Utility for debugging authentication issues
 */

/**
 * Returns detailed information about the current auth state
 */
export const debugAuthState = (): any => {
    try {
      // Check localStorage for the auth data
      const storedAuth = localStorage.getItem('destiny_auth');
      
      if (!storedAuth) {
        return {
          status: 'error',
          message: 'No auth data found in localStorage',
          localStorage: Object.keys(localStorage).filter(key => !key.includes('debug'))
        };
      }
      
      // Parse the stored auth data
      const authData = JSON.parse(storedAuth);
      
      // Create a safe version without tokens for logging
      const safeAuthData = { ...authData };
      
      if (safeAuthData.accessToken) {
        safeAuthData.accessToken = `${safeAuthData.accessToken.substring(0, 10)}...`;
      }
      
      if (safeAuthData.refreshToken) {
        safeAuthData.refreshToken = `${safeAuthData.refreshToken.substring(0, 10)}...`;
      }
      
      // Check for required fields
      const requiredFields = [
        'isAuthenticated',
        'membershipId',
        'destinyMembershipId',
        'membershipType'
      ];
      
      const missingFields = requiredFields.filter(field => !authData[field]);
      
      return {
        status: 'success',
        authState: safeAuthData,
        missingFields: missingFields.length > 0 ? missingFields : null,
        hasTokenExpired: authData.expires && authData.expires < Date.now(),
        localStorage: Object.keys(localStorage).filter(key => !key.includes('debug'))
      };
    } catch (error) {
      return {
        status: 'error',
        message: 'Error parsing auth state',
        error: error instanceof Error ? error.message : String(error),
        localStorage: Object.keys(localStorage).filter(key => !key.includes('debug'))
      };
    }
  };
  
  /**
   * Returns information about the auth callback processing
   * This should be called in AuthCallback.tsx component
   */
  export const debugAuthCallback = (code: string | null, params: URLSearchParams): any => {
    return {
      status: 'debug',
      timestamp: new Date().toISOString(),
      hasCode: Boolean(code),
      codeLength: code ? code.length : 0,
      codePrefix: code ? `${code.substring(0, 5)}...` : null,
      allParams: Array.from(params.entries()),
      error: params.get('error'),
      state: params.get('state'),
    };
  };
  
  /**
   * Sets a test auth state for development and testing
   */
  export const setTestAuthState = (): void => {
    const testAuthState = {
      isAuthenticated: true,
      accessToken: 'test_access_token_for_development',
      refreshToken: 'test_refresh_token_for_development',
      expires: Date.now() + 3600000, // One hour from now
      membershipId: '12345678901234567',
      destinyMembershipId: '9876543210987654',
      membershipType: 3, // 3 is for Steam
      displayName: 'TestGuardian'
    };
    
    localStorage.setItem('destiny_auth', JSON.stringify(testAuthState));
    console.log('Test auth state set successfully:', testAuthState);
    
    // Refresh the page to apply the changes
    window.location.reload();
  };